// An emailed share or invitation link goes straight to the log-in page or
// the registration page, carrying its token as a path segment, the way
// every other token this app handles travels: /login/share/:token,
// /login/invitation/:token, /registration/share/:token,
// /registration/invitation/:token. Each page keeps the token on its link
// to the other, so the visitor can switch without losing it. A visitor who
// is already signed in runs the session ladder like anyone else; a share
// token is held and `launch` takes it to the landing.
type EmailedLink = { kind: 'share' | 'invitation'; token: string };

// The link the current route carries, if any: the route declares its kind
// on its handle (`emailedLink: 'share' | 'invitation'`) and carries the
// token as its `token` param.
const emailedLinkFrom = (
  matches: { handle: unknown }[],
  token: string | undefined,
): EmailedLink | undefined => {
  const kind = matches
    .map(
      ({ handle }) =>
        (handle as { emailedLink?: EmailedLink['kind'] })?.emailedLink,
    )
    .find((k) => k === 'share' || k === 'invitation');
  return kind && token ? { kind, token } : undefined;
};

const landingFor = (link: EmailedLink | undefined): string | undefined => {
  if (!link) {
    return undefined;
  }
  const token = encodeURIComponent(link.token);
  return link.kind === 'share'
    ? `/shares/accept/${token}`
    : `/invitations/accept/${token}`;
};

// The log-in page for the link.
const loginFor = (link: EmailedLink): string =>
  `/login/${link.kind}/${encodeURIComponent(link.token)}`;

// The registration page for the link.
const registrationFor = (link: EmailedLink): string =>
  `/registration/${link.kind}/${encodeURIComponent(link.token)}`;

const acceptingNotice = (link: EmailedLink | undefined): string | undefined => {
  if (!link) {
    return undefined;
  }
  return link.kind === 'share'
    ? 'to accept the disclosure shared with you'
    : 'to accept your invitation';
};

// The share token a log-in or registration page arrived with, held for the
// tab until the session is finished. Accepting a share finishes nothing —
// an orphan has to create their organisation first — so the token waits
// out terms, the pending list and the orphan step, and `launch` takes it
// into the share landing, where accepting is the explicit act. Only shares
// are held: an organisation invitation is accepted in the pending list,
// which is how a registration finishes.
const STORED_SHARE = 'kinesis:share';

const storeEmailedShare = (link: EmailedLink | undefined) => {
  if (link?.kind !== 'share') {
    return;
  }
  try {
    window.sessionStorage.setItem(STORED_SHARE, link.token);
  } catch (e) {
    // Storage unavailable: the recipient still has the pending list.
  }
};

// Returns the held token, if any, and clears it.
const takeEmailedShare = (): string | undefined => {
  try {
    const token = window.sessionStorage.getItem(STORED_SHARE);
    if (token) {
      window.sessionStorage.removeItem(STORED_SHARE);
      return token;
    }
  } catch (e) {
    // Storage unavailable: nothing held.
  }
  return undefined;
};

export {
  acceptingNotice,
  emailedLinkFrom,
  landingFor,
  loginFor,
  registrationFor,
  storeEmailedShare,
  takeEmailedShare,
};
export type { EmailedLink };
